import { ExtendedMessageRole, SmartActionMessage } from '@bitte-ai/types';
import {
  CoreAssistantMessage,
  CoreToolMessage,
  Message,
  ToolInvocation,
  generateId,
} from 'ai';

export const convertResponseMessages = (
  messages: (CoreAssistantMessage | CoreToolMessage)[],
  agentId?: string,
): SmartActionMessage[] => {
  return messages.map((message) => {
    // Convert complex content to string if needed
    const content =
      typeof message.content === 'string'
        ? message.content
        : Array.isArray(message.content)
          ? message.content
              .map((part) =>
                part.type === 'text' ? part.text : JSON.stringify(part),
              )
              .join('')
          : JSON.stringify(message.content);

    // Extract only the properties we need for SmartActionMessage
    return {
      id: generateId(),
      agentId,
      content,
      role: message.role as ExtendedMessageRole,
    };
  });
};

export const getAgentIdFromMessage = (message: any): string | undefined => {
  const { annotations } = message;
  const agentIdAnnotation = annotations?.[0];

  if (agentIdAnnotation && typeof agentIdAnnotation === 'object') {
    if (
      'agentId' in agentIdAnnotation &&
      typeof agentIdAnnotation.agentId === 'string'
    ) {
      return agentIdAnnotation.agentId;
    }
  }
};

function addToolMessageToChat({
  toolMessage,
  messages,
}: {
  toolMessage: CoreToolMessage | SmartActionMessage;
  messages: Array<Message & { agentId?: string }>;
}): Array<Message> {
  return messages.map((message) => {
    if (message.parts) {
      return {
        ...message,
        parts: message.parts.map((part) => {
          if (part.type === 'tool-invocation') {
            // Handle core tool message content
            const toolResult =
              typeof toolMessage.content === 'string'
                ? null
                : Array.isArray(toolMessage.content)
                  ? toolMessage.content.find(
                      (tool) =>
                        tool.toolCallId === part.toolInvocation.toolCallId,
                    )
                  : null;

            if (toolResult) {
              return {
                ...part,
                state: 'result',
                result: toolResult.result,
              };
            }

            return part;
          }

          return part;
        }),
        annotations: message.agentId
          ? [{ agentId: message.agentId }]
          : undefined,
      };
    }

    return message;
  });
}

export function convertToUIMessages(
  messages: Array<SmartActionMessage>,
): Array<Message> {
  return messages.reduce<Array<Message>>((chatMessages, message) => {
    const annotations = message.agentId
      ? [{ agentId: message.agentId }]
      : undefined;

    if (message.role === 'tool' || message.role === 'function') {
      return addToolMessageToChat({
        toolMessage: message,
        messages: chatMessages,
      });
    }

    let textContent = '';
    const toolInvocations: Array<ToolInvocation> = [];

    if (typeof message.content === 'string') {
      textContent = message.content;
    } else if (Array.isArray(message.content)) {
      for (const content of message.content as any[]) {
        if (content.type === 'text') {
          textContent += content.text;
        } else if (content.type === 'tool-call') {
          toolInvocations.push({
            state: 'call',
            toolCallId: content.toolCallId,
            toolName: content.toolName,
            args: content.args,
          });
        }
      }
    }

    const messageRoles = ['user', 'assistant', 'system', 'data'];
    const validRole = messageRoles.includes(message.role)
      ? message.role
      : 'user';

    chatMessages.push({
      id: message.id || generateId(),
      role: validRole,
      content: textContent,
      toolInvocations,
      annotations,
      parts: message.parts,
    });

    return chatMessages;
  }, []);
}

// Function to remove ".vercel.app" from agentId
export const formatAgentId = (agentId: string) => {
  return agentId.replace('.vercel.app', '');
};
